import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { CalendarIcon } from "lucide-react";
import { format, isValid } from "date-fns";
import { Calendar } from "./Calendar";
import { cn } from "@/shared/lib/cn";

export interface DatePickerProps {
  // ISO YYYY-MM-DD string; "" means no date selected.
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  // Red border — validation error state.
  invalid?: boolean;
  // Compact sizing for table cells (px-2 py-1 text-[12px]); default is form control size.
  dense?: boolean;
  disabled?: boolean;
  // Forwarded to the input so <label htmlFor> can target it.
  id?: string;
  // Extra classes merged onto the input (e.g. amber "unsaved change" / "missing field" border).
  className?: string;
}

// Parse an ISO YYYY-MM-DD string to a local-midnight Date without UTC drift.
// date-fns parseISO parses "YYYY-MM-DD" as midnight UTC on some environments; we want local.
function parseLocalDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  // Construct from parts to stay at local midnight (avoids timezone offset shifting the day).
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  const d = new Date(year, month - 1, day);
  return isValid(d) ? d : undefined;
}

// Apply a dd.MM.yyyy input mask as the user types: keep only digits (max 8) and
// auto-insert the dots — "22081996" → "22.08.1996", "2208" → "22.08".
function maskDate(input: string): string {
  const d = input.replace(/\D/g, "").slice(0, 8);
  let out = d.slice(0, 2);
  if (d.length > 2) out += "." + d.slice(2, 4);
  if (d.length > 4) out += "." + d.slice(4, 8);
  return out;
}

// Parse a typed display string (dd.MM.yyyy; also accepts / or - separators) to a Date.
// Round-trips the parts so impossible dates (e.g. 32.13.2026) are rejected.
function parseDisplay(text: string): Date | undefined {
  const m = text.trim().match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})$/);
  if (!m) return undefined;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
    return d;
  }
  return undefined;
}

// Format a Date to an ISO YYYY-MM-DD string using local components (no UTC drift).
function formatLocalISO(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

// Format a Date to the human-readable display string shown in the input.
function formatDisplay(date: Date): string {
  return format(date, "dd.MM.yyyy");
}

// Themed date picker: a typeable text input + a calendar-icon button that opens a
// Radix Popover (portal-escaped, safe in overflow/table containers) wrapping the
// brand-themed Calendar. The calendar uses month/year dropdowns for fast navigation.
// Props mirror the Combobox contract: value/onChange as controlled ISO string, invalid, dense, disabled, id.
export function DatePicker({
  value,
  onChange,
  placeholder = "kk.oo.yyyy",
  invalid = false,
  dense = false,
  disabled = false,
  id,
  className,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  // While the user types, `draft` holds the raw text so partial input isn't clobbered
  // by the formatted value. null = not editing → show the formatted `value`.
  const [draft, setDraft] = useState<string | null>(null);

  const selected = parseLocalDate(value);
  const displayValue = draft !== null ? draft : selected ? formatDisplay(selected) : "";

  // Dropdown navigation range: wide enough for birth dates / old production dates (past)
  // and future payment dates. ~1920 → current year + 10.
  const currentYear = new Date().getFullYear();
  const startMonth = new Date(1920, 0, 1);
  const endMonth = new Date(currentYear + 10, 11, 31);

  function commitDraft(text: string) {
    const trimmed = text.trim();
    if (!trimmed) {
      onChange("");
      return;
    }
    const parsed = parseDisplay(trimmed);
    if (parsed) onChange(formatLocalISO(parsed));
    // Invalid input: keep the previous value; clearing the draft resets the field to it.
  }

  function handleSelect(date: Date | undefined) {
    onChange(date ? formatLocalISO(date) : "");
    setDraft(null);
    setOpen(false);
  }

  return (
    <Popover.Root open={open} onOpenChange={disabled ? undefined : setOpen}>
      {/* Anchor the popover to the whole input, but only the icon button triggers it. */}
      <Popover.Anchor asChild>
        <div className="relative">
          <input
            id={id}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            disabled={disabled}
            value={displayValue}
            placeholder={placeholder}
            onChange={(e) => {
              const masked = maskDate(e.target.value);
              setDraft(masked);
              // Live-sync the underlying value once a complete, valid date is typed.
              if (masked === "") {
                onChange("");
              } else {
                const parsed = parseDisplay(masked);
                if (parsed) onChange(formatLocalISO(parsed));
              }
            }}
            onBlur={() => {
              if (draft !== null) {
                commitDraft(draft);
                setDraft(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // Don't submit the surrounding <form>; commit the typed date instead.
                e.preventDefault();
                if (draft !== null) {
                  commitDraft(draft);
                  setDraft(null);
                }
              }
            }}
            className={cn(
              "w-full rounded-control border bg-white text-left outline-none transition-colors",
              "placeholder:text-grey-400 disabled:cursor-not-allowed disabled:opacity-50",
              dense ? "px-2 py-1 pr-7 text-[12px]" : "px-3.5 py-2.5 pr-10 text-[14px]",
              invalid
                ? "border-red focus-visible:border-red focus-visible:ring-1 focus-visible:ring-red-border"
                : "border-grey-200 hover:border-grey-300 focus-visible:border-green focus-visible:ring-1 focus-visible:ring-green",
              className,
            )}
          />
          <Popover.Trigger asChild>
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              aria-label="Taqvimni ochish"
              className={cn(
                "absolute inset-y-0 right-0 flex items-center text-grey-400 outline-none transition-colors",
                "hover:text-grey-600 focus-visible:text-green disabled:cursor-not-allowed disabled:opacity-50",
                dense ? "px-2" : "px-3",
              )}
            >
              <CalendarIcon size={dense ? 13 : 15} strokeWidth={1.8} className="shrink-0" />
            </button>
          </Popover.Trigger>
        </div>
      </Popover.Anchor>
      {/* Portal ensures the popover is not clipped by overflow:hidden or table containers */}
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            "z-[1000] overflow-hidden rounded-card border border-grey-200 bg-white shadow-float",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          )}
        >
          <Calendar
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            defaultMonth={selected ?? new Date()}
            captionLayout="dropdown"
            startMonth={startMonth}
            endMonth={endMonth}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
